import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [company, setCompany] = useState(null);
  const [impersonating, setImpersonating] = useState(localStorage.getItem("cs_impersonating") === "1");
  const [actingCompany, setActingCompanyState] = useState(() => {
    const id = localStorage.getItem("cs_acting_company");
    return id ? { id, name: localStorage.getItem("cs_acting_company_name") || "" } : null;
  });

  useEffect(() => {
    const token = localStorage.getItem("cs_token");
    if (!token) { setUser(false); return; }
    api.get("/auth/me")
      .then(({ data }) => { setUser(data.user); setCompany(data.company); })
      .catch(() => { localStorage.removeItem("cs_token"); setUser(false); });
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cs_token", data.token);
    setUser(data.user);
    setCompany(data.company);
    return data.user;
  };

  const logout = () => {
    ["cs_token", "cs_super_token", "cs_impersonating", "cs_acting_company", "cs_acting_company_name"].forEach((k) => localStorage.removeItem(k));
    setUser(false); setCompany(null); setImpersonating(false); setActingCompanyState(null);
  };

  const impersonate = async (companyId) => {
    const { data } = await api.post(`/companies/${companyId}/impersonate`);
    localStorage.setItem("cs_super_token", localStorage.getItem("cs_token"));
    localStorage.setItem("cs_token", data.token);
    localStorage.setItem("cs_impersonating", "1");
    localStorage.setItem("cs_acting_company_name", data.company?.name || "");
    setUser(data.user); setCompany(data.company); setImpersonating(true);
    return data.user;
  };

  const exitImpersonation = async () => {
    const st = localStorage.getItem("cs_super_token");
    if (st) localStorage.setItem("cs_token", st);
    ["cs_super_token", "cs_impersonating", "cs_acting_company_name"].forEach((k) => localStorage.removeItem(k));
    setImpersonating(false);
    const { data } = await api.get("/auth/me");
    setUser(data.user); setCompany(data.company);
    return data.user;
  };

  const setActingCompany = (c) => {
    localStorage.setItem("cs_acting_company", c.id);
    localStorage.setItem("cs_acting_company_name", c.name || "");
    setActingCompanyState({ id: c.id, name: c.name || "" });
  };

  const clearActingCompany = () => {
    localStorage.removeItem("cs_acting_company");
    localStorage.removeItem("cs_acting_company_name");
    setActingCompanyState(null);
    setCompany(null);
  };

  return (
    <AuthContext.Provider value={{ user, company, setCompany, login, logout,
      impersonating, impersonate, exitImpersonation,
      actingCompany, setActingCompany, clearActingCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
